import React from 'react';

interface TwineCandlesLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'navbar';
}

const TwineCandlesLogo: React.FC<TwineCandlesLogoProps> = ({ className = '', size = 'md' }) => {
  const sizes = {
    sm: { logoSize: 48, textSize: 'text-sm' },
    md: { logoSize: 60, textSize: 'text-base' },
    lg: { logoSize: 72, textSize: 'text-lg' },
    xl: { logoSize: 108, textSize: 'text-2xl' },
    navbar: { logoSize: 58, textSize: 'text-sm' }
  };

  const currentSize = sizes[size];

  return (
    <div className={`flex items-center ${className}`}>
      {/* Logo Graphic - Focused on the candle and circle */}
      <svg
        width={currentSize.logoSize}
        height={currentSize.logoSize}
        viewBox="380 90 1240 1040"
        className="mr-3"
      >
        <defs>
          <style>{`
            .cls-1{fill:#d4af37;stroke:#b8860b;stroke-width:3px;}
            .cls-2{fill:url(#linear-gradient);}
            .cls-3{fill:#fff;}
            .cls-4{fill:#977550;}
            .cls-5{fill:none;stroke:#c39c59;stroke-width:8px;stroke-miterlimit:10;}
            .cls-6{fill:#eddabc;}
            .cls-7{fill:none;stroke:#c4a05b;stroke-width:4px;stroke-miterlimit:10;}
            .cls-8{fill:none;stroke:#c4a05b;stroke-width:4px;stroke-linecap:round;stroke-miterlimit:10;}
            .cls-9{fill:none;stroke:#ba9957;stroke-width:4px;stroke-miterlimit:10;}
            .cls-10{fill:none;stroke:#b18540;stroke-width:4px;stroke-linecap:round;stroke-miterlimit:10;}
          `}</style>
          <linearGradient id="linear-gradient" x1="994.35" y1="638.64" x2="998.3" y2="243.52" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#c39c53"/>
            <stop offset="0.23" stopColor="#c59e56"/>
            <stop offset="0.41" stopColor="#cba65f"/>
            <stop offset="0.56" stopColor="#d5b26f"/>
            <stop offset="0.68" stopColor="#e1c082"/>
            <stop offset="0.77" stopColor="#d7b372"/>
            <stop offset="0.87" stopColor="#cfa965"/>
            <stop offset="0.97" stopColor="#cca561"/>
          </linearGradient>
        </defs>
        
        {/* Golden Circle - Outer ring */}
        <path className="cls-1" d="M1502.58,708.64c.52,357-362.6,602.06-692.82,463.76-152.24-62.9-268.45-204.12-299.89-365.9C461.41,574.1,589.6,333.6,809.3,243.76c330.4-137.17,694,107.69,693.28,464.88Zm-.5,0C1503.62,509.17,1377,323,1192.48,248.39,864.45,113.88,504.26,354,502.74,708.64c-.26,357,360.6,595.37,689.83,460.44,184.38-74.83,311.13-260.91,309.51-460.44Z"/>
        <path className="cls-1" d="M1097.2,226.61c348.61,77.07,508.88,484.77,302.12,777-94.48,134.94-257.57,217.68-422.32,213.21-237.38-3.2-444.27-180.59-484.23-414.56C430.68,450,748.7,148.32,1097.2,226.61Zm-.11.49c-194.37-44.86-403.6,38.32-516.53,202.18C378,720.24,534.08,1124,879.93,1202.55c348.4,77.84,659.5-222.6,599.34-573.28-33-196.24-187.06-360.4-382.18-402.17Z"/>
        
        {/* Candle and Flame - Bigger and more prominent */}
        <path className="cls-2" d="M992.31,579.42c-24.95,0-96.25-57.31-49.1-140,46.54-81.66,42.91-176.43,42.42-175.61,0,0,43.29,52.23,71.73,172.53C1085.89,557,1017.26,579.42,992.31,579.42Z"/>
        <path className="cls-3" d="M1003.42,414.1c-9.87,28.15-38.37,77.13-44.55,94.06-9.61,26.28,9,66.29,31.57,68.59,29.44,3,44.92-39.18,44.92-69.14S1005.19,409.05,1003.42,414.1Z"/>
        <path className="cls-4" d="M1015.08,508.16c-12.36,31.56-20.66,63.47-14.69,97.46-17.45-29.87-7.63-72.65,14.69-97.46Z"/>
        
        {/* Candle Body - Made bigger */}
        <path className="cls-5" d="M1062.93,962.4l-1.64-304.17a36.05,36.05,0,0,0-29.74-35.11l-73.39-13.5a26.22,26.22,0,0,0-31,25.79V953.18"/>
        <path className="cls-6" d="M1034,962.4l-1-282.54c0-16-7.22-29.68-17.08-32.61l-42.14-12.54c-9.25-2.76-17.78,8.73-17.78,23.95V953.84"/>
        
        {/* Twine Spiral - Made more visible */}
        <ellipse className="cls-7" cx="992.5" cy="1010.5" rx="92.33" ry="19.76"/>
        <path className="cls-8" d="M1075.92,897c16.94,5.38,49.12,12.82,45.9,36.08-3.27,23.66-38.66,34-57.55,38.82-37.81,9.69-78.62,10.68-117.14,5-16.64-2.47-33.32-6.31-48.86-12.87-10.57-4.46-21.87-10.41-28.85-19.84-17.17-23.21,13.93-47.28,32.8-55.72a101.74,101.74,0,0,1,13.24-4.84"/>
        <ellipse className="cls-9" cx="995.06" cy="1059.13" rx="85.57" ry="18.31"/>
        <path className="cls-10" d="M984.16,1097.42s71.67-17.34,84.07,2.64-68.06,19.33-82.65,22.83-33.25,21.95-19.86,45.87S989.65,1203,989.65,1203"/>
      </svg>
      
      {/* Text - Separate from SVG with proper dark/light mode */}
      <div className="flex flex-col">
        <div className={`${currentSize.textSize} font-serif font-bold leading-tight text-gray-800 dark:text-white`}>
          Twine
        </div>
        <div className={`${currentSize.textSize} font-sans font-light tracking-wider leading-tight text-gray-700 dark:text-gray-300`}>
          Candles
        </div>
      </div>
    </div>
  );
};

export default TwineCandlesLogo;
